import { AsyncPipe, DatePipe, NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PrimeTemplate } from 'primeng/api';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';
import { DrawerModule } from 'primeng/drawer';
import { SelectModule } from 'primeng/select';
import { Subscription } from 'rxjs';
import { ExcelService } from 'src/app/core/services/excel.service';
import { ExcelRequestColumn } from 'src/app/models/ExcelRequestColumn';
import { FilterResponse } from 'src/app/models/FilterResponse';
import { QrCodeDialogComponent } from 'src/app/shared/components/qr-code-dialog/qr-code-dialog.component';
import { LoadingComponent } from 'src/app/utils/loading/loading.component';
import { UtilsService } from 'src/app/utils/utils.service';
import { ColumnFilterDirective } from '../../../shared/directives/p-columnFilter.directive';
import { TableDirective } from '../../../shared/directives/p-table.directive';
import { CardRequestDetailService } from '../card-request-detail/card-request-detail.service';
import { NewCardRequestComponent } from "../new-card-request/new-card-request.component";
import { NewCardRequestService } from '../new-card-request/new-card-request.service';
import { CardRequestService } from './card-request.service';
import { CardRequestsActiveFiltersComponent } from './card-requests-active-filters/card-requests-active-filters.component';
import { CardRequestsFilterService } from './card-requests-filter.service';

@Component({
    selector: 'app-card-requests',
    templateUrl: './card-requests.component.html',
    standalone: true,
    imports: [
        NgIf,
        DrawerModule,
        PrimeTemplate,
        FormsModule,
        ReactiveFormsModule,
        TableModule,
        TableDirective,
        TooltipModule,
        CardRequestsActiveFiltersComponent,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        ColumnFilterDirective,
        SelectModule,
        DatePickerModule,
        NgFor,
        SkeletonModule,
        TranslateModule,
        AsyncPipe,
        DatePipe,
        NewCardRequestComponent,
        LoadingComponent,
        QrCodeDialogComponent
    ]
})
export class CardRequestsComponent implements OnInit, OnDestroy {

    isLoading = false;
    filterResponse: FilterResponse;
    sidebarVisible = false;
    private readonly filterHandlerSubscription!: Subscription;
	@ViewChild('table') table: Table;

	constructor(
        private readonly router: Router,
		public readonly cardRequestsFilterService: CardRequestsFilterService,
		public readonly cardRequestService: CardRequestService,
        public readonly cardRequestDetailService: CardRequestDetailService,
		public readonly newCardRequestService: NewCardRequestService,
		public readonly utilsService: UtilsService,
        private readonly excelService: ExcelService,
        private readonly translate: TranslateService,
        private readonly datePipe: DatePipe
	) {
	    this.filterHandlerSubscription = this.cardRequestsFilterService.filterHandler().subscribe((filteredResponse: FilterResponse) => {
	        this.filterResponse = filteredResponse;
	    });
	}

	async ngOnInit(): Promise<void> {
	    await this.cardRequestsFilterService.search();
	}

	ngOnDestroy(): void {
	    if (this.filterHandlerSubscription) {
	        this.filterHandlerSubscription.unsubscribe();
	    }
	}

	goToDetail(queryId: string): void {
	    this.isLoading = true;
	    this.router.navigate([`/cards/request-detail/${queryId}`])
	        .catch(error => { console.error(error); })
	        .finally(() => this.isLoading = false);
	}

	async exportToExcel(): Promise<void> {
	    const cols: ExcelRequestColumn[] = [
	        {
	            key: 'queryId',
	            i18n: 'MY_REQUESTS.QUERY_ID',
	            width:36
	        },
	        {
	            key: 'queryType',
	            i18n: 'FILTER.REQUEST_TYPE',
	            value: (r) => r.queryType === 'PhysicalPerson' ? this.translate.instant('FILTER.QUERY_TYPES.PHYSICAL_PERSON') : r.queryType
	        },
	        {
	            key: 'personId',
	            i18n: 'MY_REQUESTS.ID',
	            width: 12
	        },
	        {
	            key: 'phoneNumber',
	            i18n: 'FILTER.PHONE',
	            width: 12
	        },
	        {
	            key: 'createdDateTimeUTC',
	            i18n: 'MY_REQUESTS.REQUEST_DATE_UTC',
	            width: 22,
	            value: (r) => this.datePipe.transform(r.createdDateTime, 'dd/MM/yyyy HH:mm:ss', 'UTC')
	        },
	        {
	            key: 'requestReference',
	            i18n: 'MY_REQUESTS.USER',
	            width: 12
	        },
		    {
	            key: 'statusi18n',
	            i18n: 'MY_REQUESTS.STATUS',
	            width: 14,
	            value: (r) => this.translate.instant(`STATUS.${r.status}`)
	        },
	        {
	            key: 'substatusi18n',
	            i18n: 'MY_REQUESTS.SUBSTATUS',
	            width: 34,
	            value: (r) => this.translate.instant(`SUBSTATUS.${r.substatus}`)
	        }
	    ];

	    await this.excelService.exportList(this.filterResponse?.requestList, cols);
	}

	newRequest(): void {
	    this.newCardRequestService.myForm.reset();
	    this.sidebarVisible = true;
	}

	globalSearch($event: Event): void {
	    const input = $event.target as HTMLInputElement;
	    this.cardRequestsFilterService.globalSearchSubject.next({ inputValue: input.value, inputFallBackReference: input });
	}

	checkFilterFieldHasValue(field: string): boolean {
	    return this.cardRequestsFilterService.form.get(field).value != null && this.cardRequestsFilterService.form.get(field).value != '';
	}

	showIfTableIsLoadingAndEmpty(): boolean {
	    return (this.filterResponse?.requestList?.length == 0 || this.filterResponse == null) && this.cardRequestsFilterService.isLoading;
	}

	onSortChange(event: any): void {
	    const field = event.field;
	    const order = event.order;
	    this.cardRequestsFilterService.setSortField(field, order);
	}

	async sync(): Promise<void> {
	    this.cardRequestsFilterService.resetSortField();
	    if (this.table) {
	        this.table.reset();	// refresh the table to show the new data
	    }
	    await this.cardRequestsFilterService.search(true);
	}
}
